import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid, MenuItem, TextField } from "@mui/material";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

const selectFieldProps = {
  InputLabelProps: { shrink: true },
  sx: {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      alignItems: "center",
    },
  },
};

function UpdatePreviousYearQuestion() {
  const id = window.location.pathname.split("/")[2];
  const [redirect, setRedirect] = useState(false);
  const [notification, setNotification] = useState({ open: false, color: "success", message: "" });
  const [allExams, setAllExams] = useState([]);
  const [allYears, setAllYears] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [form, setForm] = useState({
    exam_id: "",
    year_id: "",
    subject_id: "",
    topic_id: "",
    question_pattern: "mcq",
    question_type: "medium",
    question: "",
    question_hindi: "",
    option1: "",
    option1_hindi: "",
    option2: "",
    option2_hindi: "",
    option3: "",
    option3_hindi: "",
    option4: "",
    option4_hindi: "",
    answer: "option1",
    description: "",
    description_hindi: "",
    time: 60,
    status: "active",
  });

  const showMessage = (message, color = "success", shouldRedirect = false) => {
    setNotification({ open: true, color, message });
    setTimeout(() => {
      setNotification({ open: false, color, message: "" });
      if (shouldRedirect) setRedirect(true);
    }, 2500);
  };

  const loadSelects = async () => {
    try {
      const [examResp, yearResp, subjectResp] = await Promise.all([
        axios.get(Constants.getUrls.getPyqMetaExams),
        axios.get(Constants.getUrls.getPyqMetaYears),
        axios.get(Constants.getUrls.getPyqMetaSubjects),
      ]);
      setAllExams(examResp?.data?.data || []);
      setAllYears(yearResp?.data?.data || []);
      setAllSubjects(subjectResp?.data?.data || []);
    } catch (error) {
      showMessage("Unable to load exam, year or subject options.", "error");
    }
  };

  useEffect(() => {
    loadSelects();
  }, []);

  useEffect(() => {
    const loadItem = async () => {
      if (!id || init() !== "success") return;
      try {
        const resp = await axios.get(`${Constants.getUrls.getAllPreviousYearQuestions}/${id}`);
        const data = resp?.data?.data || {};
        setForm({
          exam_id: data?.exam_id?._id || data?.exam_id || "",
          year_id: data?.year_id?._id || data?.year_id || "",
          subject_id: data?.subject_id?._id || data?.subject_id || "",
          topic_id: data?.topic_id?._id || data?.topic_id || "",
          question_pattern: data?.question_pattern || "mcq",
          question_type: data?.question_type || "medium",
          question: data?.question || "",
          question_hindi: data?.question_hindi || "",
          option1: data?.option1 || "",
          option1_hindi: data?.option1_hindi || "",
          option2: data?.option2 || "",
          option2_hindi: data?.option2_hindi || "",
          option3: data?.option3 || "",
          option3_hindi: data?.option3_hindi || "",
          option4: data?.option4 || "",
          option4_hindi: data?.option4_hindi || "",
          answer: data?.answer || "option1",
          description: data?.description || "",
          description_hindi: data?.description_hindi || "",
          time: data?.time || 60,
          status: data?.status || "active",
        });
      } catch (error) {
        showMessage(error?.response?.data?.message || "Unable to load previous year question.", "error");
      }
    };
    loadItem();
  }, [id]);

  useEffect(() => {
    const loadTopics = async () => {
      if (!form.subject_id) {
        setAllTopics([]);
        return;
      }
      try {
        const resp = await axios.get(`${Constants.getUrls.getPyqMetaTopics}?subject_id=${form.subject_id}`);
        setAllTopics(resp?.data?.data || []);
      } catch (error) {
        showMessage("Unable to load topics.", "error");
      }
    };
    loadTopics();
  }, [form.subject_id]);

  const handleChange = (name, value) => {
    if (name === "subject_id") {
      setForm((prev) => ({ ...prev, subject_id: value, topic_id: "" }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${Constants.putUrls.updatePreviousYearQuestion}${id}`, form);
      showMessage("Previous year question updated successfully!", "success", true);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to update previous year question.", "error");
    }
  };

  if (redirect) return <Navigate to="/previous-year-questions" />;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
            <MDTypography variant="h6" color="white">
              Update Previous Year Question
            </MDTypography>
          </MDBox>
          <MDBox py={3} px={2}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Exam"
                    value={form.exam_id}
                    onChange={(e) => handleChange("exam_id", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="">Select Exam</MenuItem>
                    {allExams.map((item) => (
                      <MenuItem key={item._id} value={item._id}>{item.exam_name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Year"
                    value={form.year_id}
                    onChange={(e) => handleChange("year_id", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="">Select Year</MenuItem>
                    {allYears.map((item) => (
                      <MenuItem key={item._id} value={item._id}>{item.year}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Subject (Optional)"
                    value={form.subject_id}
                    onChange={(e) => handleChange("subject_id", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="">No Subject</MenuItem>
                    {allSubjects.map((item) => (
                      <MenuItem key={item._id} value={item._id}>{item.subject_name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Topic (Optional)"
                    value={form.topic_id}
                    onChange={(e) => handleChange("topic_id", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="">No Topic</MenuItem>
                    {allTopics.map((item) => (
                      <MenuItem key={item._id} value={item._id}>{item.topic_name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField fullWidth label="Question" value={form.question} onChange={(e) => handleChange("question", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Question Hindi" value={form.question_hindi} onChange={(e) => handleChange("question_hindi", e.target.value)} />
                </Grid>
                {["option1", "option2", "option3", "option4"].map((optionKey, index) => (
                  <React.Fragment key={optionKey}>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label={`Option ${index + 1}`} value={form[optionKey]} onChange={(e) => handleChange(optionKey, e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label={`Option ${index + 1} Hindi`} value={form[`${optionKey}_hindi`]} onChange={(e) => handleChange(`${optionKey}_hindi`, e.target.value)} />
                    </Grid>
                  </React.Fragment>
                ))}
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Answer"
                    value={form.answer}
                    onChange={(e) => handleChange("answer", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="option1">Option 1</MenuItem>
                    <MenuItem value="option2">Option 2</MenuItem>
                    <MenuItem value="option3">Option 3</MenuItem>
                    <MenuItem value="option4">Option 4</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Question Pattern"
                    value={form.question_pattern}
                    onChange={(e) => handleChange("question_pattern", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="mcq">MCQ</MenuItem>
                    <MenuItem value="passage">Passage</MenuItem>
                    <MenuItem value="statement">Statement</MenuItem>
                    <MenuItem value="assertion_reason">Assertion & Reason</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Question Type"
                    value={form.question_type}
                    onChange={(e) => handleChange("question_type", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="easy">Easy</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="hard">Hard</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField type="number" fullWidth label="Time (seconds)" value={form.time} onChange={(e) => handleChange("time", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={form.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Description" value={form.description} onChange={(e) => handleChange("description", e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Description Hindi" value={form.description_hindi} onChange={(e) => handleChange("description_hindi", e.target.value)} />
                </Grid>
              </Grid>
              <Grid align="left" mt={2}>
                <MDButton variant="contained" size="small" color="info" type="submit">
                  Update
                </MDButton>
              </Grid>
            </form>
          </MDBox>
          <MDSnackbar color={notification.color} icon="notifications" title="AgriQuiz" content={notification.message} dateTime="Just now" open={notification.open} />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default UpdatePreviousYearQuestion;
